import React from 'react';
import { Box, Tooltip } from '@mui/material';
import { useAppTheme } from '../../hooks/useAppTheme';
import { CH_TYPE } from '../../styles/appUi';
import { useAccountStats, openPnlExplain } from '../../stores/accountStatsStore';
import { SEVERITY_COLOR, type LiquidationSeverity } from '../../hooks/useLiquidationRisk';
import { formatSignedCurrency, formatCompactInr, formatCurrency } from '../../utils/formatters';

/**
 * ChartTitleAccountStats — the account figures, rendered as the tail of the chart title.
 *
 * WHY HERE, AND NOT IN THE HEADER
 * The obvious home was the top bar next to "Updated Ns ago". It is the wrong one. That
 * toolbar is already out of width — the clock and the sync stamp hide below 1400px and the
 * Open P&L pill below 1280px, because every group in it sets `flexShrink: 0` and they paint
 * over each other rather than yielding (there is a smoke check for exactly that collision).
 * Five more figures in there would have regressed the header on every laptop.
 *
 * The chart title has the room, is already the place the eye goes for "what am I looking at
 * and what is it doing", and is theme-aware — unlike the AppBar, which is a fixed dark navy
 * in BOTH themes, so the light-mode palette (deep greens and reds, tuned for white) would
 * have been close to unreadable on it.
 *
 * SUBSCRIBES DIRECTLY. `accountStatsStore` is ref-counted, so this costs no extra polling —
 * it shares one set of timers with the header pill, the Risk Cockpit and the liquidation
 * banner. Render it on the PRIMARY pane only: these are account-level figures, and repeating
 * them on four quad panes would be noise pretending to be data.
 */

/** The severity ladder above which the number itself goes red, not just the dot. */
const SEVERE = new Set<LiquidationSeverity>(['WARNING', 'CRITICAL', 'EMERGENCY']);

const Stat: React.FC<{
  label: string;
  tooltip: string;
  color?: string;
  children: React.ReactNode;
}> = ({ label, tooltip, color, children }) => {
  const { C } = useAppTheme();
  return (
    <Tooltip title={tooltip} arrow>
      <Box component="span" sx={{ display: 'inline-flex', alignItems: 'baseline', gap: 0.35, cursor: 'help' }}>
        <Box component="span" sx={{
          fontSize: CH_TYPE.micro, fontWeight: 700, letterSpacing: 0.4,
          color: C.dim, textTransform: 'uppercase',
          fontFamily: (t) => t.typography.fontFamily,
        }}>{label}</Box>
        <Box component="span" sx={{
          fontWeight: 800, color: color || 'text.primary',
          fontVariantNumeric: 'tabular-nums',
        }}>{children}</Box>
      </Box>
    </Tooltip>
  );
};

const ChartTitleAccountStats: React.FC = () => {
  const { C, isDark } = useAppTheme();
  const a = useAccountStats();

  const sev = (a.risk?.severity || 'SAFE') as LiquidationSeverity;
  // `null` (not resolved) and `available: false` (NOT EVALUATED) are both unknown. Neither
  // may render as green — that is the exact failure this whole feature exists to prevent.
  const liqKnown = !!a.risk?.available;
  const liqColor = liqKnown ? SEVERITY_COLOR[sev] : C.dim;
  const liqText = !liqKnown ? '—' : a.risk?.mmr == null ? 'n/a' : `${a.risk.mmr.toFixed(0)}%`;
  const liqTip = !liqKnown
    ? 'Liquidation risk has not been evaluated yet (no snapshot, or no active Delta credentials). This is NOT a confirmation that you are safe.'
    : a.risk?.mmr == null
      ? 'No collateral posted, so there is no maintenance-margin ratio to report.'
      : `Maintenance-margin ratio ${a.risk.mmr.toFixed(1)}% — the venue liquidates at 100%. Severity ${sev}.`
        + (a.risk?.bufferPct != null ? ` A ${a.risk.bufferPct.toFixed(1)}% adverse move would reach liquidation.` : '')
        + (a.risk?.stale ? ' This reading is stale.' : '');

  const sign = (v: number | null) => v == null ? 'text.primary' : v >= 0 ? C.bull : C.bear;
  const dot = <Box component="span" sx={{
    width: 7, height: 7, borderRadius: '50%', bgcolor: liqColor, display: 'inline-block',
    mr: 0.4, flexShrink: 0,
    // A severe state should catch the eye without the user looking for it.
    ...(liqKnown && SEVERE.has(sev) ? {
      animation: 'liqPulse 1.6s infinite',
      '@keyframes liqPulse': { '0%,100%': { opacity: 1 }, '50%': { opacity: 0.35 } },
    } : {}),
  }} />;

  return (
    <Box sx={{
      display: 'inline-flex', alignItems: 'baseline', gap: 0.9,
      // The legend as a whole is pointer-events:none so it never eats a click on the chart.
      // The rail opts back IN, because each figure carries a tooltip that explains it.
      pointerEvents: 'auto',
      pl: 1, ml: 0.4,
      borderLeft: `1px solid ${isDark ? 'rgba(255,255,255,0.18)' : 'rgba(15,23,42,0.18)'}`,
      fontSize: CH_TYPE.small,
      whiteSpace: 'nowrap',
    }}>
      <Stat label="P&L" color={sign(a.openPnl)}
        tooltip={openPnlExplain(a, v => formatCurrency(v, 'INR', 2))}>
        {formatSignedCurrency(a.openPnl, 'INR', 0)}
      </Stat>
      <Stat label="Day" color={sign(a.todayPnl)}
        tooltip={`Today's REALISED P&L — trades closed today; open positions are in the P&L figure beside it${a.todayPnlPct != null ? ` (${a.todayPnlPct >= 0 ? '+' : ''}${a.todayPnlPct.toFixed(2)}%)` : ''}.`}>
        {formatSignedCurrency(a.todayPnl, 'INR', 0)}
      </Stat>
      <Stat label="Port"
        tooltip={`Portfolio value across Delta + Zerodha. Exact: ${a.portfolioValue == null ? 'not loaded' : formatCurrency(a.portfolioValue, 'INR', 2)}`}>
        {formatCompactInr(a.portfolioValue)}
      </Stat>
      <Stat label="Margin"
        color={a.availableMargin != null && a.availableMargin <= 0 ? C.bear : undefined}
        tooltip={`Available margin — free collateral you can still deploy. Exact: ${a.availableMargin == null ? 'not loaded' : formatCurrency(a.availableMargin, 'INR', 2)}`}>
        {formatCompactInr(a.availableMargin)}
      </Stat>
      <Tooltip title={liqTip} arrow>
        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.35, cursor: 'help' }}>
          {dot}
          <Box component="span" sx={{
            fontSize: CH_TYPE.micro, fontWeight: 700, letterSpacing: 0.4, color: C.dim,
            textTransform: 'uppercase', fontFamily: (t) => t.typography.fontFamily,
          }}>Liq</Box>
          <Box component="span" sx={{
            fontWeight: 800, fontVariantNumeric: 'tabular-nums',
            color: liqKnown && SEVERE.has(sev) ? liqColor : 'text.primary',
          }}>{liqText}</Box>
        </Box>
      </Tooltip>
    </Box>
  );
};

export default ChartTitleAccountStats;
